import { useCallback, useEffect, useState } from 'react';
import {
	fetchManager,
	fetchGameweekPicks,
	saveManagerUsername,
	ApiError,
} from '@/lib/api.js';

/* Dev fixture, loaded dynamically so neither the module nor its data is
 * reachable from the production bundle. See lib/devFixture.js. */
async function devFixturePayload() {
	if (!import.meta.env.DEV) return null;
	if (!new URLSearchParams(window.location.search).has('mock')) return null;
	const { managerFixture } = await import('@/lib/devFixture.js');
	return managerFixture;
}

const FIRST_GW = 1;
const LAST_GW = 38;

/*
 * Owns the state that ManagerController held on $scope.
 *
 * Two distinct loads, as in the original:
 *   load()        — full payload for a manager (GET /api/manager)
 *   goToGameweek()— picks only, for GW navigation (POST /api/fixtures),
 *                   which patches a subset of fields and leaves the
 *                   season-level stats and charts untouched.
 */
export function useManager(initialUsername) {
	const [data, setData] = useState(null);
	const [picks, setPicks] = useState({ playing: null, bench: null });
	const [status, setStatus] = useState('idle'); // idle | loading | ready | error
	const [navigating, setNavigating] = useState(false);
	const [error, setError] = useState(null);
	/* Set when the API names why it failed, so the page can tell a genuine
	 * failure apart from a state like the season not having started. */
	const [errorReason, setErrorReason] = useState(null);
	const [saveState, setSaveState] = useState({ status: 'idle', message: null });

	const load = useCallback(async ({ managerId, username } = {}) => {
		if (!managerId && !username) {
			setStatus('idle');
			return;
		}
		setStatus('loading');
		setError(null);
		setErrorReason(null);
		try {
			const payload =
				(await devFixturePayload()) ?? (await fetchManager({ managerId, username }));
			setData(payload);
			setPicks({ playing: payload.playing_picks, bench: payload.bench_picks });
			setStatus('ready');
		} catch (err) {
			setError(err instanceof ApiError ? err.message : 'Could not load that team.');
			setErrorReason(err instanceof ApiError ? (err.body?.reason ?? null) : null);
			setStatus('error');
		}
	}, []);

	/* Load once on mount when the route carries a username. */
	useEffect(() => {
		if (initialUsername) load({ username: initialUsername });
	}, [initialUsername, load]);

	const goToGameweek = useCallback(
		async (direction) => {
			if (!data) return;
			const target = direction === 'prev' ? data.gw - 1 : data.gw + 1;
			if (target < FIRST_GW || target > LAST_GW) return;

			setNavigating(true);
			try {
				const next = await fetchGameweekPicks({
					managerId: data.manager_id,
					playingPicks: picks.playing,
					benchPicks: picks.bench,
					gw: target,
				});
				setData((prev) => ({
					...prev,
					gw: next.gw,
					gw_or: next.gw_or,
					points: next.points,
					cost: next.cost,
					captain: next.captain,
					vicecaptain: next.vicecaptain,
				}));
				setPicks({ playing: next.playing_picks, bench: next.bench_picks });
			} catch (err) {
				setError(
					err instanceof ApiError ? err.message : 'Could not load that gameweek.',
				);
			} finally {
				setNavigating(false);
			}
		},
		[data, picks],
	);

	const saveUsername = useCallback(async ({ managerId, username }) => {
		setSaveState({ status: 'saving', message: null });
		try {
			const result = await saveManagerUsername({ managerId, username });
			setSaveState({ status: 'saved', message: null });
			return result;
		} catch (err) {
			setSaveState({
				status: 'error',
				message: err instanceof ApiError ? err.message : 'Could not save username.',
			});
			return null;
		}
	}, []);

	return {
		data,
		picks,
		status,
		error,
		errorReason,
		navigating,
		saveState,
		load,
		goToGameweek,
		saveUsername,
		canGoPrev: Boolean(data) && data.gw > FIRST_GW,
		canGoNext: Boolean(data) && data.gw < LAST_GW,
		/* points is absent for gameweeks that have not started. */
		showPoints: Boolean(data) && data.points !== undefined && data.points !== null,
	};
}
